import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { db } from '@util/firebase';
import { createChat } from '@util/firestore/messages';
import {
  InitiateReturnArgs,
  addReturnNote,
  approveReturn,
  cancelReturn,
  completeReturn,
  denyReturn,
  getReturnById,
  getReturns,
  getReturnsByOrderId,
  getReturnsByUser,
  getWarehouseReturns,
  initiateRefund,
  initiateReturnRequest,
  reopenReturn,
} from '@util/firestore/returns/returns.service';
import { doc, updateDoc } from 'firebase/firestore';
import { AddressDocument } from 'models/address';
import { OrderDocument } from 'models/order';
import { ReturnDocument, ReturnState } from 'models/returns';
import { useAuth } from 'context/AuthContext';
import { ChatMessageInput, chatMessagesSchema } from 'models/chat';
export function useReturns({
  orderId,
  uid,
  field,
  returnId,
  state,
  onMarkedAsReceived,
  onApproved,
}: {
  orderId?: string;
  uid?: string;
  field?: 'buyer_id' | 'seller_id';
  returnId?: string;
  state?: ReturnState;
  onMarkedAsReceived?: (success: boolean) => void;
  onApproved?: (success: boolean) => void;
} = {}) {
  const queryClient = useQueryClient();
  const { userDoc } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['returns', state, userDoc?.uid],
    queryFn: async () => {
      const returns = await getReturns({ state });
      return returns;
    },
    enabled:
      userDoc?.uid === process.env.NEXT_PUBLIC_SUPPORT_ID ||
      userDoc?.uid === 'qlcEanuNfBZOpI7CmwgLN0QKPlo1', // socal,
  });

  const { data: returnsByOrder } = useQuery({
    queryKey: ['returnRequest', orderId, uid],
    queryFn: async () => {
      if (!orderId || !uid) return null;
      const returnRequests = await getReturnsByOrderId(
        orderId,
        uid,
        field ?? 'buyer_id'
      );
      return returnRequests;
    },
    enabled: !!orderId && !!uid,
  });

  const { data: returnsByUser, isLoading: isLoadingReturnsByUser } = useQuery({
    queryKey: ['returnsByUser', uid, state],
    queryFn: async () => {
      if (!uid || !field) return null;
      const returns = await getReturnsByUser(uid, field, state);
      return returns;
    },
    enabled: !!uid && !!field,
    throwOnError: true,
  });

  const { data: returnById, isLoading: isLoadingReturnById } = useQuery({
    queryKey: ['returnById', returnId],
    queryFn: async () => {
      if (!returnId) return null;
      const returnRequest = await getReturnById(returnId);
      return returnRequest;
    },
    enabled: !!returnId,
  });

  const { data: warehouseReturns } = useQuery({
    queryKey: ['warehouseReturns', state, userDoc?.uid],
    queryFn: async () => {
      const returns = await getWarehouseReturns(state);
      return returns;
    },
    enabled:
      userDoc?.uid === process.env.NEXT_PUBLIC_SUPPORT_ID ||
      userDoc?.uid === 'qlcEanuNfBZOpI7CmwgLN0QKPlo1', // socal,
  });

  const markAsReceivedMutation = useMutation({
    mutationFn: async ({
      id,
      received_by_warehouse,
      warehouse_comments,
      images,
      is_warehouse_return,
      order_id,
    }: Pick<
      ReturnDocument,
      | 'id'
      | 'received_by_warehouse'
      | 'warehouse_comments'
      | 'images'
      | 'is_warehouse_return'
      | 'order_id'
    >) => {
      await updateDoc(doc(db, 'returns', id), {
        received_by_warehouse,
        warehouse_comments,
        images,
      });
      if (
        received_by_warehouse === 'needs_attention' &&
        !is_warehouse_return &&
        userDoc?.uid
      ) {
        const messages = [];
        const initialMessage: ChatMessageInput = {
          created_at: Date.now(),
          content: `Return arrived not as described`,
          uid: userDoc.uid,
        };
        if (warehouse_comments) {
          initialMessage.content += `: ${warehouse_comments}`;
        }
        const parsedInitialMessage = chatMessagesSchema.parse(initialMessage);
        messages.push(parsedInitialMessage);

        if (images) {
          const imageMessages = images.map((image) => {
            const message: ChatMessageInput = {
              created_at: Date.now(),
              content: image.thumb,
              is_img: true,
              uid: userDoc.uid,
            };
            const parsedMessage = chatMessagesSchema.parse(message);
            return parsedMessage;
          });
          messages.push(...imageMessages);
        }
        await createChat({
          created_at: Date.now(),
          buyer_id: process.env.NEXT_PUBLIC_SUPPORT_ID!,
          seller_id: userDoc.uid,
          messages,
          last_time: Date.now(),
          id: '',
          uids: [process.env.NEXT_PUBLIC_SUPPORT_ID!, userDoc.uid],
          order_id,
          unread: {
            [process.env.NEXT_PUBLIC_SUPPORT_ID!]: true,
          },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
      onMarkedAsReceived?.(true);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const initiateReturnRequestMutation = useMutation({
    mutationFn: async (args: InitiateReturnArgs) => {
      await initiateReturnRequest(args);
    },
  });

  const approveReturnMutation = useMutation({
    mutationFn: async ({
      id,
      ship_to,
    }: {
      id: string;
      ship_to: AddressDocument;
    }) => {
      await approveReturn({ id, ship_to });
      return { id };
    },
    onSuccess: async ({ id }) => {
      alert('Return Approved');
      onApproved?.(true);
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const denyReturnMutation = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      return await denyReturn({ id });
    },
    onSuccess: (success) => {
      if (success) {
        alert('Return Denied');
        queryClient.invalidateQueries({ queryKey: ['returns'] });
      }
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const initiateRefundMutation = useMutation({
    mutationFn: async ({
      order,
      returnDoc,
      amount,
    }: {
      order: OrderDocument;
      returnDoc: ReturnDocument;
      amount: number;
    }) => {
      return await initiateRefund({ order, returnDoc, amount });
    },
    onSuccess: async ({ id, amount }) => {
      alert('Refund Initiated');
      queryClient.invalidateQueries({ queryKey: ['returns'] });
      await addReturnNote({
        id,
        note: `Refund initiated by admin for ${amount}`,
      });
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const completeReturnMutation = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await completeReturn({ id });
    },
    onSuccess: () => {
      alert('Return Completed');
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const cancelReturnMutation = useMutation({
    mutationFn: async ({ returnDoc }: { returnDoc: ReturnDocument }) => {
      await cancelReturn({ returnDoc });
    },
    onSuccess: () => {
      alert('Return Cancelled');
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
  });

  const reopenReturnMutation = useMutation({
    mutationFn: async ({ returnDoc }: { returnDoc: ReturnDocument }) => {
      await reopenReturn({ returnDoc });
    },
    onSuccess: () => {
      alert('Return Reopened');
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  return {
    data,
    isLoading,
    approveReturnMutation,
    denyReturnMutation,
    initiateRefundMutation,
    returnsByOrder,
    returnsByUser,
    returnById,
    isLoadingReturnsByUser,
    isLoadingReturnById,
    completeReturnMutation,
    warehouseReturns,
    markAsReceivedMutation,
    cancelReturnMutation,
    reopenReturnMutation,
  };
}
